import { Component, Input, OnInit } from '@angular/core';
import { FormGroup, Validators, UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { UserService } from '../../shared/services/user.service';
import { faEnvelope } from '@fortawesome/pro-light-svg-icons';
import { ThemePalette } from '@angular/material/core';
import { DateTime } from 'luxon';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastService } from 'src/app/shared/services/toast.service';

@Component({
  selector: 'app-user-details',
  templateUrl: './user-details.component.html',
  styleUrls: ['./user-details.component.scss']
})
export class UserDetailsComponent implements OnInit {

  @Input() userId?: number;
  @Input() internalUser: boolean = false;
  @Input() companyGroupId?: number;

  companyGroups: any[] = [];
  roles: any[] = [];
  teamLeaders: any[] = [];
  inTeamleaderList = false;

  newUser: boolean = true;

  sendingPasswordReset: boolean = false;
  sendingInvitation: boolean = false;
  updating: boolean = false;

  pageTitle: string = 'Internal User';

  //for the colour picker
  public disabled = false;
  public color: ThemePalette = 'primary';
  public touchUi = false;

  public faEnvelope = faEnvelope;

  colorCtr: UntypedFormControl = new UntypedFormControl(null);

  userLoaded = false;

  lastLoginTime: string = '';

  userDetails = this.fb.group({
    id: [],
    firstName: ['', Validators.required],
    lastName: ['', Validators.required],
    emailAddress: ['', [Validators.required, Validators.email]],
    companyGroup: [''],
    companyGroupId: ['', Validators.required],
    role: [null, Validators.required],
    accountDisabled: [null],
    isTeamLeader: [false, Validators.required],
    teamLeaderUserId: null,
    hasAPIAccess: [''],
    applicationKey: [''],
    canAllocateCases: [null],
    canReallocateCases: [null],
    canSeeAdminActions: [null],
    isAMLOfficer: [null],
    position: [''],
    klyantApiClientId: null,
    klyantApiSecret: [null],
    colour: [null],
    individualStatusId: new UntypedFormControl({ value: null, disabled: true })
  }, {});

  constructor(private fb: UntypedFormBuilder,
              private userService: UserService,
              private toastService: ToastService,
              public activeModal: NgbActiveModal) {


      

      this.colorCtr!.valueChanges.subscribe((newColour: any) => {
        this.userDetails.controls['colour'].patchValue(newColour);
      });

  }

  ngOnInit(): void {


    if (!this.internalUser) {
      this.pageTitle = 'External User';

      if (this.companyGroupId) {
        // pre-set and skip loading the dropdown
        this.userDetails.controls['companyGroupId'].patchValue(this.companyGroupId);
      } else {
        this.userService.companyGroups().subscribe(result => {
          this.companyGroups = result;
          this.companyGroups.splice(0, 1); //Remove the first item (which will be Ad Law)
        });
    }
    } else {
      // Internal is Ad law
      this.userDetails.controls['companyGroupId'].patchValue(1);
    }

    if (this.userId)
    {
      this.newUser = false;
      this.loadUser();
    }
    else
      this.initialiseUser();
  }

  loadUser(){


        this.userService.userDetails(this.userId).subscribe(result => {

          this.userDetails.patchValue(result);
          this.userDetails.markAllAsTouched();
          
          this.inTeamleaderList = true;

          this.userService.teamLeaders().subscribe(result => {
            this.teamLeaders = result;

            this.teamLeaders = this.teamLeaders.filter( tl => tl.role ==  this.userDetails.controls['role'].value);
          });

        this.populateRoles();

        // Initialise the colour picker
        const colourValue = result.colour;
        if(this.internalUser && colourValue != null && colourValue != "") {
          this.colorCtr.setValue(colourValue.startsWith('#') ? colourValue : '#' + colourValue);
        }

        this.lastLoginTime = this.formatDate(result!.lastLogin);
        this.userLoaded = true;

      }, error => {


      });



  }

  initialiseUser(){

      // default values for new users
      this.userDetails.controls['hasAPIAccess'].patchValue(false);
      this.userDetails.controls['accountDisabled'].patchValue(false);

      // Show requried fields
      Object.keys(this.userDetails.controls).forEach(key => {

        this.userDetails.get(key)!.markAsTouched();
        this.userDetails.get(key)!.updateValueAndValidity();
      });

      this.userService.teamLeaders().subscribe(result => {
        this.teamLeaders = result;
        this.teamLeaders = this.teamLeaders.filter( tl => tl.role ==  this.userDetails.controls['role'].value);
      });

      this.populateRoles();
      this.userLoaded = true;
  }

  saveUser() {

    this.updating = true;
    this.userService.updateUser(this.userId, this.userDetails.value).subscribe(response => {

      this.activeModal.close(true);

    }, error => {

      var errorMessage = "User Update Failed";
      if (error.error !== null && error.error.message !== undefined) {
        errorMessage = error.error.message;
      }

      this.toastService.error(errorMessage);
      this.updating = false;


    });
  }

  addUser() {

    this.sendingInvitation = true;
    this.userService.addUser(this.userDetails.value).subscribe(response => {

      this.sendingInvitation = false;
      this.activeModal.close(true);

    }, error => {

      console.log(error);

      var errorMessage = "User Creation Failed";
      if(error.status == 999)
      errorMessage = error.error;
      else
      {

      if (error.error !== null && error.error.message !== undefined) {
        errorMessage = error.error.message;
      }
    }


     this.toastService.error(errorMessage);

      this.sendingInvitation = false;
    });
  }

  updateTeamLeaders(){

    this.userService.teamLeaders().subscribe(result => {
      this.teamLeaders = result;
      this.teamLeaders = this.teamLeaders.filter( tl => tl.role ==  this.userDetails.controls['role'].value);
      this.userDetails.controls['teamLeaderUserId'].patchValue(-1);
    });
  }

  get teamLeadersForRole() {
    return this.teamLeaders.filter( tl => tl.role ==  this.userDetails.controls['role'].value);
  }

  populateRoles(){
    if(this.internalUser) {
      this.userService.internalRoles().subscribe(result => {
        this.roles = result;
      });
    }
    else{
      this.userService.externalRoles().subscribe(result => {
        this.roles = result;
      });
    }
  }


  resetPassword() {

    this.sendingPasswordReset = true;
    this.userService.resetPassword({ emailAddress: this.userDetails.controls['emailAddress'].value }).subscribe(response => {

      this.sendingPasswordReset = false;
       this.toastService.success("Password Reset Email Sent");

    }, error => {
      this.sendingPasswordReset = false;

      var errorMessage = "Password Reset Failed";
      if (error.error !== null) {
        errorMessage = error.error.message
      }

      this.toastService.error(errorMessage);

    });

  }

  formatDate(inDate : any){

    var formattedDate = '';

     if(inDate !== null)
     {
       var dt = DateTime.fromISO(inDate);
       formattedDate = dt.toFormat("dd-MM-yyyy HH:mm:ss");

     if(formattedDate == 'Invalid date')
       formattedDate = '';
     }

     return formattedDate;
   }

}
